import { Box, Button, DialogActions, DialogContent, DialogTitle, Dropdown, FormControl, Input, Menu, MenuButton, MenuItem, Modal, ModalDialog, Table, Typography } from "@mui/joy";
import React from "react";
import { Util } from "../Helpers/Util";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import WorkOrderDialog from "../Dialogs/WorkOrderDialog";
import ReassignPackingListDialog from "../Dialogs/ReassignPackingListDialog";
import AssignmentHistoryDialog from "../Dialogs/AssignmentHistoryDialog";
import SignatureCanvas from "react-signature-canvas";
import WrappedButton from "../WrappedButton";

// Interface for packing list data
export interface PackingList {
	BuildingName: string;
	InvoiceNumber: string;
	SerialNumber: string;
	Mechanic: string;
	InvoiceHeaderID: string;
	PackingListID: string;
	Notes: string;
	HasSignature: boolean;
	ApprovedDate: string;
	ApprovedDateRaw: Date | null;
	TypeOfWork: string;
}

// Type for sortable columns
type SortColumn = "BuildingName" | "InvoiceNumber" | "SerialNumber" | "Mechanic" | "ApprovedDate";
type SortDirection = "asc" | "desc";

/**
 * Component for displaying approved packing lists
 * Shows a table of packing lists that have been signed/approved
 */
export default function ApprovedPackingLists() {
	const [data, setData] = React.useState<Array<PackingList>>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [workOrderModal, setWorkOrderModal] = React.useState(false);
	const [currentPackingListId, setCurrentPackingListId] = React.useState<string>("");
	const [currentTypeOfWork, setCurrentTypeOfWork] = React.useState("");
	const [currentNotes, setCurrentNotes] = React.useState("");
	const [reassignModal, setReassignModal] = React.useState(false);
	const [historyModal, setHistoryModal] = React.useState(false);
	const [currentMechanic, setCurrentMechanic] = React.useState("");
	const [currentInvoiceNumber, setCurrentInvoiceNumber] = React.useState("");
	const [sortColumn, setSortColumn] = React.useState<SortColumn | null>(null);
	const [sortDirection, setSortDirection] = React.useState<SortDirection>("asc");
	const [signModal, setSignModal] = React.useState(false);
	const [isSaving, setIsSaving] = React.useState(false);
	const [currentBuildingName, setCurrentBuildingName] = React.useState("");

	let sigRef: any = null;

	// Fetch approved packing lists on component mount
	React.useEffect(() => {
		getData();
	}, []);

	/**
	 * Fetches approved packing lists from the API
	 */
	const getData = () => {
		fetch("PackingList/GetPackingLists", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ approved: true }),
		})
			.then((x) => x.json())
			.then((x) => {
				setData(
					x.map((y: any) => {
						return {
							BuildingName: y.building_name,
							InvoiceNumber: y.invoice_number,
							SerialNumber: y.serial_number,
							Mechanic: y.mechanic_name,
							InvoiceHeaderID: y.invoice_header_id,
							PackingListID: y.packing_list_id,
							Notes: y.notes ?? "",
							HasSignature: y.has_signature ?? false,
							ApprovedDate: y.approved_date ? new Date(y.approved_date).toLocaleDateString() : "",
							ApprovedDateRaw: y.approved_date ? new Date(y.approved_date) : null,
							TypeOfWork: y.proposal_line_item_type ?? "",
						} as PackingList;
					})
				);
			});
	};

	/**
	 * Opens the work order dialog for a packing list
	 */
	const openWorkOrderDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentBuildingName(inv.BuildingName);
		setCurrentInvoiceNumber(inv.InvoiceNumber);
		setCurrentTypeOfWork(inv.TypeOfWork);
		setCurrentNotes(inv.Notes);
		setWorkOrderModal(true);
	};

	/**
	 * Opens the reassign dialog for a packing list
	 */
	const openReassignDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentMechanic(inv.Mechanic);
		setReassignModal(true);
	};

	/**
	 * Opens the assignment history dialog for a packing list
	 */
	const openHistoryDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentInvoiceNumber(inv.InvoiceNumber);
		setHistoryModal(true);
	};

	/**
	 * Opens the signed PDF for a given packing list in a new tab
	 */
	const openSignedPdf = (inv: PackingList) => {
		fetch("PackingList/GetSignedPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				packingListId: inv.PackingListID,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				const blob = x;
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + blob;
			});
	};

	/**
	 * Opens the unsigned PDF with the right side whited out
	 * Calls GetUnsignedPdf which fetches from QB and applies white-out
	 */
	const openUnsignedPdf = (inv: PackingList) => {
		fetch("PackingList/GetUnsignedPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				estimateNumber: inv.InvoiceNumber,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + x;
			});
	};

	/**
	 * Opens the signature modal for an unsigned approved packing list
	 */
	const openSignDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentInvoiceNumber(inv.InvoiceNumber);
		setCurrentBuildingName(inv.BuildingName);
		setSignModal(true);
	};

	/**
	 * Saves the signature for an already-approved packing list
	 * Calls SignApprovedPackingList which generates the signed PDF
	 */
	const saveSignature = () => {
		if (sigRef.isEmpty()) {
			alert("A signature is required.");
			return;
		}
		setIsSaving(true);

		var dt = {
			base64Image: sigRef.toDataURL(),
			packingListId: currentPackingListId,
			estimateNumber: currentInvoiceNumber,
			buildingName: currentBuildingName,
		};

		fetch("PackingList/SignApprovedPackingList", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(dt),
		}).then((x) => {
			setSignModal(false);
			setIsSaving(false);
			getData();
		});
	};

	/**
	 * Handles column header click for sorting
	 */
	const handleSort = (column: SortColumn) => {
		if (sortColumn === column) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortColumn(column);
			setSortDirection("asc");
		}
	};

	/**
	 * Renders sort indicator icon for column headers
	 */
	const renderSortIcon = (column: SortColumn) => {
		if (sortColumn !== column) return null;
		return sortDirection === "asc" ? (
			<ArrowUpwardIcon sx={{ fontSize: 16, verticalAlign: "middle", ml: 0.5 }} />
		) : (
			<ArrowDownwardIcon sx={{ fontSize: 16, verticalAlign: "middle", ml: 0.5 }} />
		);
	};

	const filteredData = data?.filter(
		(x) =>
			Util.isNullOrEmpty(globalFilter) ||
			x.BuildingName.toLowerCase().includes(globalFilter.toLowerCase()) ||
			x.InvoiceNumber.toString().includes(globalFilter) ||
			x.Mechanic.toLowerCase().includes(globalFilter.toLowerCase())
	);

	// Calculate pagination values based on filtered results
	const maxPages = Math.ceil((filteredData ?? []).length / 25);

	// Apply sorting
	const sortedData = filteredData?.sort((a, b) => {
		if (!sortColumn) return 0;
		let comparison = 0;
		if (sortColumn === "ApprovedDate") {
			const dateA = a.ApprovedDateRaw?.getTime() ?? 0;
			const dateB = b.ApprovedDateRaw?.getTime() ?? 0;
			comparison = dateA - dateB;
		} else {
			comparison = a[sortColumn].localeCompare(b[sortColumn]);
		}
		return sortDirection === "asc" ? comparison : -comparison;
	});

const thisRows = sortedData?.slice(pageNumber * 25, (pageNumber + 1) * 25);

return (
  <>
    <WorkOrderDialog
      open={workOrderModal}
      packingListId={currentPackingListId}
      clientName={currentBuildingName}
      estimateNumber={currentInvoiceNumber}
      typeOfWork={currentTypeOfWork}
      initialNotes={currentNotes}
      onClose={() => setWorkOrderModal(false)}
      onSaved={() => {
        setWorkOrderModal(false);
        getData();
      }}
    />
    <ReassignPackingListDialog
      open={reassignModal}
      packingListId={currentPackingListId}
      currentMechanic={currentMechanic}
      onClose={() => setReassignModal(false)}
      onSaved={() => {
        setReassignModal(false);
        getData();
      }}
    />
    <AssignmentHistoryDialog
      open={historyModal}
      packingListId={currentPackingListId}
      invoiceNumber={currentInvoiceNumber}
      onClose={() => setHistoryModal(false)}
    />
    <Modal open={signModal}>
      <ModalDialog>
        <DialogTitle>Signature</DialogTitle>
        <DialogContent>
          <SignatureCanvas
            ref={(ref: any) => {
              sigRef = ref;
            }}
            penColor="black"
            canvasProps={{ width: "900", height: "400", style: { border: "1px solid black", borderRadius: "8px" } }}
          ></SignatureCanvas>
        </DialogContent>
        <DialogActions>
          <WrappedButton onClick={saveSignature} busy={isSaving}>
            Save Signature
          </WrappedButton>
          <Button
            onClick={() => {
              setSignModal(false);
            }}
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
    <Box sx={{ overflowY: "auto", padding: 2 }}>
      <div style={{ display: "flex" }}>
        <div style={{ flex: 1 }}>
          <Typography level="title-lg">Assigned Work Orders</Typography>
        </div>
        <FormControl sx={{ marginRight: 1 }}>
          <Input
            name="Search"
            placeholder="Global Search"
            onChange={(event) => {
              setGlobalFilter(event.target.value);
              setPageNumber(0);
            }}
            value={globalFilter}
          />
        </FormControl>
      </div>
      <Table stripe={"odd"} hoverRow>
        <thead>
          <tr>
            <th style={{ cursor: "pointer" }} onClick={() => handleSort("BuildingName")}>
              Building Name
              {renderSortIcon("BuildingName")}
            </th>
            <th style={{ cursor: "pointer" }} onClick={() => handleSort("InvoiceNumber")}>
              Estimate #
              {renderSortIcon("InvoiceNumber")}
            </th>
            <th style={{ cursor: "pointer" }} onClick={() => handleSort("SerialNumber")}>
              Serial #
              {renderSortIcon("SerialNumber")}
            </th>
            <th style={{ cursor: "pointer" }} onClick={() => handleSort("Mechanic")}>
              Mechanic
              {renderSortIcon("Mechanic")}
            </th>
            <th style={{ cursor: "pointer" }} onClick={() => handleSort("ApprovedDate")}>
              Approved Date
              {renderSortIcon("ApprovedDate")}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {thisRows?.map((x, idx) => {
            return (
              <tr key={idx} style={{ cursor: "pointer" }}>
                <td>{x.BuildingName}</td>
                <td>{x.InvoiceNumber}</td>
                <td>{x.SerialNumber}</td>
                <td>{x.Mechanic}</td>
                <td>{x.ApprovedDate}</td>
                <td>
                  <Dropdown>
                    <MenuButton variant="plain" size="sm">
                      <MoreVertIcon />
                    </MenuButton>
                    <Menu>
                      {!x.HasSignature && <MenuItem onClick={() => openUnsignedPdf(x)}>Open PDF</MenuItem>}
                      {!x.HasSignature && (
                        <MenuItem onClick={() => openSignDialog(x)}>Sign Packing List</MenuItem>
                      )}
                      {x.HasSignature && <MenuItem onClick={() => openSignedPdf(x)}>Open Signed PDF</MenuItem>}
                      <MenuItem onClick={() => openWorkOrderDialog(x)}>Work Order</MenuItem>
                      <MenuItem onClick={() => openReassignDialog(x)}>Reassign Tech</MenuItem>
                      <MenuItem onClick={() => openHistoryDialog(x)}>View History</MenuItem>
                    </Menu>
                  </Dropdown>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={6}>
              <div style={{ float: "right" }}>
                <span>
                  <KeyboardArrowLeftIcon
                    onClick={() => pageNumber > 0 && setPageNumber(pageNumber - 1)}
                    sx={{ verticalAlign: "middle", cursor: "pointer" }}
                  />{" "}
                  {`Page ${pageNumber + 1} of ${maxPages}`}{" "}
                  <KeyboardArrowRightIcon
                    onClick={() => maxPages > pageNumber + 1 && setPageNumber(pageNumber + 1)}
                    sx={{ verticalAlign: "middle", cursor: "pointer" }}
                  />
                </span>
              </div>
            </td>
          </tr>
        </tfoot>
      </Table>
    </Box>
  </>
);
}
